import React, { useState, useEffect } from "react";
import {
  Typography, Grid, TextField,
  Accordion, AccordionSummary, AccordionDetails,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SelectConAggiunta from "../SelectConAggiunta";
import {
  getAccountsCRM, creaAccountCRM,
  getOpportunitaPerAccount, creaOpportunitaManuale
} from "../../utils/suiteCRM";

const CLIENTI_CONTRATTO = {
  "cdp": "2022000508 - Contratto 2022",
  "consorziostandby": "2024000220 - Contratto 2024",
  "consorzio standby": "2024000220 - Contratto 2024",
  "eligo": "Accordo Quadro 2021",
};

function getContrattoDefault(nomeCliente) {
  if (!nomeCliente) return null;
  const nome = nomeCliente.toLowerCase();
  for (const [chiave, valore] of Object.entries(CLIENTI_CONTRATTO)) {
    if (nome.includes(chiave)) return valore;
  }
  return null;
}

export default function SezioneRiferimenti({ form, handleChange, direzioni, loadingDirezioni, aggiungiDirezione, clienti }) {
  const [accountsCRM, setAccountsCRM] = useState([]);
  const [loadingCRM, setLoadingCRM] = useState(false);
  const [opportunitaCRM, setOpportunitaCRM] = useState([]);
  const [loadingOpp, setLoadingOpp] = useState(false);

  const [dialogAccountOpen, setDialogAccountOpen] = useState(false);
  const [nuovoAccount, setNuovoAccount] = useState("");
  const [creandoAccount, setCreandoAccount] = useState(false);

  const [dialogOppOpen, setDialogOppOpen] = useState(false);
  const [nuovaOpp, setNuovaOpp] = useState({ nome: "", data: "", importo: "" });
  const [creandoOpp, setCreandoOpp] = useState(false);

  useEffect(() => {
    setLoadingCRM(true);
    getAccountsCRM()
      .then((accounts) => setAccountsCRM(accounts))
      .finally(() => setLoadingCRM(false));
  }, []);

  useEffect(() => {
    if (!form.accountCRMId) {
      setOpportunitaCRM([]);
      return;
    }
    setLoadingOpp(true);
    getOpportunitaPerAccount(form.accountCRMId)
      .then((opps) => setOpportunitaCRM(opps))
      .finally(() => setLoadingOpp(false));
  }, [form.accountCRMId]);

  useEffect(() => {
    if (opportunitaCRM.length > 0 && form.opportunitaCRM) {
      const oppSelezionata = opportunitaCRM.find((o) => o.id === form.opportunitaCRM);
      if (oppSelezionata?.importo && !form.importoVendita) {
        handleChange("importoVendita", oppSelezionata.importo);
      }
    }
  }, [opportunitaCRM]);

  const clienteSelezionato = clienti?.find((c) => c.id === form.clienteId);
  const contrattoDefault = getContrattoDefault(clienteSelezionato?.nome);
  const mostraContratto = contrattoDefault !== null;

  useEffect(() => {
    if (contrattoDefault && !form.contrattoRif) {
      handleChange("contrattoRif", contrattoDefault);
    }
    if (!mostraContratto && form.contrattoRif) {
      handleChange("contrattoRif", "");
    }
  }, [form.clienteId]);

  async function handleCreaAccount() {
    if (!nuovoAccount.trim()) return;
    setCreandoAccount(true);
    try {
      const id = await creaAccountCRM(nuovoAccount.trim());
      if (id) {
        const nuovoItem = { id, nome: nuovoAccount.trim() };
        setAccountsCRM((prev) => [...prev, nuovoItem].sort((a, b) => a.nome.localeCompare(b.nome, "it")));
        handleChange("accountCRMId", id);
        setDialogAccountOpen(false);
        setNuovoAccount("");
      }
    } catch (error) {
      console.error("Errore creazione account:", error);
    }
    setCreandoAccount(false);
  }

  async function handleCreaOpportunita() {
    if (!nuovaOpp.nome.trim()) return;
    setCreandoOpp(true);
    try {
      const opp = await creaOpportunitaManuale(
        nuovaOpp.nome.trim(),
        form.accountCRMId,
        nuovaOpp.data,
        nuovaOpp.importo
      );
      if (opp) {
        setOpportunitaCRM((prev) => [...prev, opp].sort((a, b) => a.nome.localeCompare(b.nome, "it")));
        handleChange("opportunitaCRM", opp.id);
        if (opp.importo) handleChange("importoVendita", opp.importo);
        setDialogOppOpen(false);
        setNuovaOpp({ nome: "", data: "", importo: "" });
      }
    } catch (error) {
      console.error("Errore creazione opportunità:", error);
    }
    setCreandoOpp(false);
  }

  return (
    <Accordion defaultExpanded elevation={1} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "#EEEDFE" }}>
        <Typography fontWeight="bold">Riferimenti</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} md={6}>
            <SelectConAggiunta label="Direzione di riferimento" value={form.direzioneId || ""}
              onChange={(v) => handleChange("direzioneId", v)}
              items={direzioni} loading={loadingDirezioni}
              onAggiungi={aggiungiDirezione} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="Preventivo di riferimento" value={form.preventivoRif || ""}
              onChange={(e) => handleChange("preventivoRif", e.target.value)} />
          </Grid>

          {/* ACCOUNT CRM */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Account CRM</InputLabel>
              <Select
                value={form.accountCRMId || ""}
                label="Account CRM"
                onChange={(e) => {
                  if (e.target.value === "__nuovo__") {
                    setDialogAccountOpen(true);
                  } else {
                    handleChange("accountCRMId", e.target.value);
                    handleChange("opportunitaCRM", "");
                  }
                }}
                disabled={loadingCRM}
                renderValue={(selected) => {
                  if (!selected) return "— Nessuno —";
                  const account = accountsCRM.find((a) => a.id === selected);
                  return account?.nome || selected;
                }}
              >
                <MenuItem value="">— Nessuno —</MenuItem>
                {accountsCRM.map((a) => (
                  <MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>
                ))}
                <MenuItem value="__nuovo__" sx={{ color: "primary.main", fontWeight: "bold" }}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Aggiungi nuovo account...
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* OPPORTUNITÀ CRM */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Opportunità CRM</InputLabel>
              <Select
                value={form.opportunitaCRM || ""}
                label="Opportunità CRM"
                onChange={(e) => {
                  if (e.target.value === "__nuova__") {
                    setDialogOppOpen(true);
                  } else {
                    handleChange("opportunitaCRM", e.target.value);
                    const oppSelezionata = opportunitaCRM.find((o) => o.id === e.target.value);
                    if (oppSelezionata?.importo) {
                      handleChange("importoVendita", oppSelezionata.importo);
                    }
                  }
                }}
                disabled={loadingOpp || !form.accountCRMId}
                renderValue={(selected) => {
                  if (!selected) return "— Nessuna —";
                  const opp = opportunitaCRM.find((o) => o.id === selected);
                  return opp ? `${opp.nome}${opp.stage ? ` (${opp.stage})` : ""}` : selected;
                }}
              >
                <MenuItem value="">— Nessuna —</MenuItem>
                {opportunitaCRM.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.nome} {o.stage ? `(${o.stage})` : ""}
                  </MenuItem>
                ))}
                {form.accountCRMId && (
                  <MenuItem value="__nuova__" sx={{ color: "primary.main", fontWeight: "bold" }}>
                    <AddIcon fontSize="small" sx={{ mr: 1 }} />
                    Crea nuova opportunità...
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {mostraContratto && (
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Contratto di riferimento"
                value={form.contrattoRif || contrattoDefault}
                onChange={(e) => handleChange("contrattoRif", e.target.value)} />
            </Grid>
          )}

          {/* NUMERO FATTURA — visibile solo quando stato è completato */}
          {form.stato === "completato" && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numero fattura"
                value={form.numFattura || ""}
                onChange={(e) => handleChange("numFattura", e.target.value)}
              />
            </Grid>
          )}
        </Grid>
      </AccordionDetails>

      {/* DIALOG NUOVO ACCOUNT */}
      <Dialog open={dialogAccountOpen} onClose={() => setDialogAccountOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nuovo account CRM</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Nome account" value={nuovoAccount}
            onChange={(e) => setNuovoAccount(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreaAccount()}
            sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAccountOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCreaAccount}
            disabled={creandoAccount || !nuovoAccount.trim()}>
            {creandoAccount ? <CircularProgress size={20} /> : "Crea"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG NUOVA OPPORTUNITÀ */}
      <Dialog open={dialogOppOpen} onClose={() => setDialogOppOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuova opportunità CRM</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <TextField autoFocus fullWidth label="Nome opportunità" value={nuovaOpp.nome}
                onChange={(e) => setNuovaOpp((prev) => ({ ...prev, nome: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Data chiusura prevista" type="date"
                value={nuovaOpp.data}
                onChange={(e) => setNuovaOpp((prev) => ({ ...prev, data: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Importo (€)" type="number"
                value={nuovaOpp.importo}
                onChange={(e) => setNuovaOpp((prev) => ({ ...prev, importo: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOppOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleCreaOpportunita}
            disabled={creandoOpp || !nuovaOpp.nome.trim()}>
            {creandoOpp ? <CircularProgress size={20} /> : "Crea"}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}